/* eslint-disable no-console */
import { constants } from '@wsb/guac-widget-core';
import { merge } from 'lodash';

const {
  buttons: { decorations, fills }
} = constants;

export const resetCTE = {
  position: 'unset',
  opacity: 'unset',
  zIndex: 'unset',
  border: 'unset',
  transition: 'unset'
};

function underline(props) {
  if (props.fill !== fills.OPEN) {
    console.warn('Button: underline decoration must be used with shapes.OPEN.');
    return {};
  }

  return {
    style: {
      borderWidth: 0,
      borderBottomWidth: props.customBorderWidth,
      borderStyle: 'solid',
      borderColor: 'currentColor',
      paddingTop: 0,
      paddingBottom: 'xxsmall',
      alignItems: 'flex-end'
    }
  };
}

function arrow({ fill }) {
  if (fill !== fills.OPEN) {
    console.warn('Button: arrow decoration must be used with shapes.OPEN.');
    return {};
  }

  return {
    icon: 'chevronRight',
    iconOnLeft: false,
    iconSize: 'small',
    style: {
      paddingVertical: 0,
      ['svg']: {
        transform: 'translateX(0)',
        transition: 'transform .5s ease'
      },
      [':hover svg']: {
        transform: 'translateX(4px)'
      }
    }
  };
}

function lines({ size }) {
  if (size === 'small') {
    return {};
  }

  const reset = this.base.renderMode === constants.renderModes.EDIT ? resetCTE : {};
  const dash = {
    content: `""`,
    display: 'inline-block',
    height: 0.1, // Prevent it to be invisible
    width: 18,
    borderTop: '1px solid !important'
  };
  return {
    style: {
      alignItems: 'center',
      [':before']: {
        marginRight: 'xsmall',
        ...dash
      },
      [':after']: {
        marginLeft: 'xsmall',
        ...reset,
        ...dash
      }
    }
  };
}

function underlineWithArrow(props) {
  if (props.fill !== fills.OPEN) {
    console.warn('Button: underline with arrow decoration must be used with shapes.OPEN.');
    return {};
  }

  return merge(
    {
      style: {
        ['svg']: {
          marginTop: '-2px'
        },
        ['.iconText']: {
          borderWidth: 0,
          borderBottomWidth: props.customBorderWidth,
          borderStyle: 'solid',
          borderColor: 'currentColor',
          paddingBottom: 'xxsmall'
        }
      }
    },
    arrow.call(this, props)
  );
}

// iOS/WebKit drops the packed `-webkit-mask` shorthand (image + position + /size + repeat
// in one declaration), so the wavy underline never renders on real mobile. Split into
// longhands — identical result on desktop, a form WebKit reliably parses on mobile.
const WAVY_DOT_TOP =
  'radial-gradient(5.04px at 50% calc(100% + 2.7px),#0000 calc(99% - 2px),#000 calc(101% - 2px) 99%,#0000 101%)';
const WAVY_DOT_BOTTOM =
  'radial-gradient(5.04px at 50% -2.7px,#0000 calc(99% - 2px),#000 calc(101% - 2px) 99%,#0000 101%)';
const WAVY_MASK_IMAGE = `${WAVY_DOT_TOP}, ${WAVY_DOT_BOTTOM}`;
const WAVY_MASK_POSITION = 'calc(50% - 6px) calc(50% - 2.5px + .5px), 50% calc(50% + 2.5px)';
const WAVY_MASK_SIZE = '12px 5px, 12px 5px';
const WAVY_MASK_REPEAT = 'repeat-x, repeat-x';

function wavyUnderline() {
  const reset = this.base.renderMode === constants.renderModes.EDIT ? resetCTE : {};
  return {
    style: {
      position: 'relative',
      [':after']: {
        ...reset,
        top: 'unset',
        content: '""',
        position: 'absolute',
        bottom: '0.3em',
        left: 0,
        width: 'calc(100% + 12px)',
        height: '1em',
        backgroundColor: 'currentColor',
        maskImage: WAVY_MASK_IMAGE,
        maskPosition: WAVY_MASK_POSITION,
        maskSize: WAVY_MASK_SIZE,
        maskRepeat: WAVY_MASK_REPEAT,
        WebkitMaskImage: WAVY_MASK_IMAGE,
        WebkitMaskPosition: WAVY_MASK_POSITION,
        WebkitMaskSize: WAVY_MASK_SIZE,
        WebkitMaskRepeat: WAVY_MASK_REPEAT
      },
      // Only apply on true hover pointers. Touch devices emulate :hover on tap and
      // "stick" it until the next tap, so gating on (hover: hover) keeps the rest state on mobile.
      ['@media (hover: hover) and (pointer: fine)']: {
        [':hover']: {
          [':after']: {
            animation: 'wavy-shift 0.3s linear infinite'
          }
        }
      }
    }
  };
}

const options = {
  [decorations.NONE]: () => ({}),
  [decorations.UNDERLINE]: underline,
  [decorations.ARROW]: arrow,
  [decorations.LINES]: lines,
  [decorations.UNDERLINE_WITH_ARROW]: underlineWithArrow,
  [decorations.WAVY_UNDERLINE]: wavyUnderline
};

export default function (props) {
  const { decoration = decorations.NONE } = props;

  if (!decorations[decoration]) {
    console.error(`Button: decoration type of ${decoration} is not valid.`);
    return {};
  }

  return options[decoration].call(this, props);
}
